import { apiSlice } from './apiSlice'
import type {
  AnnexCreateIn,
  AnnexListQuery,
  AnnexOut,
  AnnexUpdateIn,
  Bank,
  BankListQuery,
  ContractAllItem,
  ContractAllListQuery,
  DataResponse,
  LocalPCreateIn,
  LocalPDetail,
  LocalPForwardCreateIn,
  LocalPForwardListQuery,
  LocalPForwardOut,
  LocalPForwardUpdateIn,
  LocalPListItem,
  LocalPListQuery,
  LocalPPlanCreateIn,
  LocalPPlanListItem,
  LocalPPlanListQuery,
  LocalPPlanOut,
  LocalPPlanUpdateIn,
  LocalPPrintChooseData,
  LocalPPrintChooseIn,
  LocalPPrintKind,
  LocalPPrintResult,
  LocalPUpdateIn,
  LocalPVer2Item,
  LocalPVer2ListQuery,
  MessageResponse,
  NPEAllocationCommitIn,
  NPEAllocationLineIn,
  NPEAllocationListQuery,
  NPEAllocationOut,
  NPEAllocationSessionState,
  NPEContractCreateIn,
  NPEContractListQuery,
  NPEContractOut,
  NPEContractUpdateIn,
  NPEFixationAllocationsCountOut,
  NPEFixationCreateAndAllocateOut,
  NPEFixationCreateIn,
  NPEFixationListQuery,
  NPEFixationOut,
  NPEFixationSuggestOut,
  NPEFixationUpdateIn,
  PTBFFixationCreateIn,
  PTBFFixationListQuery,
  PTBFFixationOut,
  PTBFFixationUpdateIn,
  PTBFRollingCreateIn,
  PTBFRollingListQuery,
  PTBFRollingOut,
  PTBFRollingUpdateIn,
  PageResponse,
} from '@/types/api'
import { baseApi } from './baseApi'
import { safePathEncode } from '@/utils/safePath'
// ===================== purchaseContractApi =====================
export interface PurchaseContractPayload {
  contractNo: string
  contractDate: string
  supplierCode: string
  supplierName: string
  sellersRef: string
  nptRef: string
  noOfBags: number | null
  qtyKg: number | null
  certification: string
  licenseCertNo: string
  si: string
  sd: string
  meOrIb: string
  contractType: string
  packageCode: string
  packageName: string
  priceUnit: string
  outrightPrice: number | null
  ptbfDiffs: string
  applicableVat: boolean
  vatRate: string
  fx: string
  futuresMonth: string
  fixationCall: string
  latestDelivery: string
  deliveryTerm: string
  deliveryPlace: string
  deliveryPlaceName: string
  logisticCost: string
  note: string
  traderInCharge: string
  fixedFxIfHave: string
  provOnMarketPrice: number | null
  stoplossRate: number | null
  depositValuePct: number | null
  depositLimitPct: number | null
  depositBonusPct: number | null
  interestPctPerMonth: number | null
  timeLimit: string
  repaymentDate: string
  intermediary: boolean
  issued: boolean
  sentMail: boolean
  receivedMail: boolean
  sentPaper: boolean
  receivedPaper: boolean
  shipperCode: string
  shipperName: string
  contractRef: string
  contractShipperIntermediary: boolean
  outrightShipperIntermediary: boolean
  ptbfdiffsShipperIntermediary: boolean
}

export interface PurchaseContract extends PurchaseContractPayload {
  id: string
  status: 'Draft' | 'Cancelled'
  createdAt: string
  updatedAt: string
  createdBy: string
  rawPurchaseSheetRow?: Record<string, string | null>
}

export interface PurchaseContractListResponse {
  items: PurchaseContract[]
  total: number
  page: number
  pageSize: number
}

export interface PurchaseContractListQuery {
  page: number
  pageSize: number
  q?: string
  status?: string
  supplierCode?: string
  contractType?: string
  certification?: string
}

export interface PurchaseContractPagePreferences {
  deliveryLatestFrom: string
  deliveryLatestTo: string
  isForcedClose: 'all' | 'yes' | 'no'
}

export interface PurchaseContractColumnsPreference {
  columns: string[] | null
  defaultColumns: string[] | null
}

export type PurchaseContractColumnsMode =
  | 'pucontract'
  | 'forward-pu'
  | 'forward-list'
  | 'npe-contract'
  | 'contracts-all'
  | 'annex'
  | 'ptbf-fixation'
  | 'npe-fixation'
  | 'npe-allocation'
  | 'ptbf-rolling'
  | 'cs-fixation'
  | 'local-p-plans'
  | 'ref-ValueUpdates'
  | 'ref-FuturesList'
  | 'ref-CP-List'
  | 'ref-Delivery-terms'
  | 'ref-Futures'
  | 'ref-Bank'
  | 'ref-License'
  | 'ref-FuturesLotAdjust'
  | 'ref-ListAndValo'
  | 'ref-Package'
  | 'ref-Delivery-Place'
  | 'ref-Certification'

export interface PurchaseRepInfoItem {
  id: number
  name?: string | null
  position?: string | null
}

export interface PurchaseRepInfoResponse {
  items: PurchaseRepInfoItem[]
  currentSignId?: number | null
}

export interface PurchaseContractListFiltersPatch {
  deliveryLatestFrom?: string
  deliveryLatestTo?: string
  isForcedClose?: 'all' | 'yes' | 'no'
}

export type PurchaseReferenceRow = Record<string, unknown>

export interface PurchaseReferenceRowsQuery {
  pageKey: string
  limit?: number
}

export interface PurchaseReferencePageResponse {
  pageKey: string
  sourceForm: string
  sourceQuery: string
  items: PurchaseReferenceRow[]
  total: number
}

export const purchaseContractApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPurchaseContracts: builder.query<PurchaseContractListResponse, PurchaseContractListQuery>({
      query: ({ page, pageSize, q, status, supplierCode, contractType, certification }) => ({
        url: '/purchase-contracts',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(status ? { status } : {}),
          ...(supplierCode ? { supplierCode } : {}),
          ...(contractType ? { contractType } : {}),
          ...(certification ? { certification } : {}),
        },
      }),
      providesTags: ['User'],
    }),
    createPurchaseContract: builder.mutation<PurchaseContract, PurchaseContractPayload>({
      query: (body) => ({
        url: '/purchase-contracts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    updatePurchaseContract: builder.mutation<PurchaseContract, { id: string; body: PurchaseContractPayload }>({
      query: ({ id, body }) => ({
        url: `/purchase-contracts/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    cancelPurchaseContract: builder.mutation<PurchaseContract, string>({
      query: (id) => ({
        url: `/purchase-contracts/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    getPurchaseReferenceRows: builder.query<PurchaseReferencePageResponse, PurchaseReferenceRowsQuery>({
      query: ({ pageKey, limit = 1000000 }) => ({
        url: `/references/${encodeURIComponent(pageKey)}`,
        params: { limit },
      }),
      providesTags: (_r, _e, { pageKey }) => [
        { type: 'PurchaseReference' as const, id: pageKey },
        { type: 'PurchaseReference' as const, id: 'LIST' },
      ],
    }),
    getPurchaseContractPagePreferences: builder.query<PurchaseContractPagePreferences, void>({
      query: () => ({ url: '/purchase-contracts/page-preferences' }),
      providesTags: ['PurchaseContractPrefs'],
    }),
    getPurchaseContractColumnsPreference: builder.query<PurchaseContractColumnsPreference, PurchaseContractColumnsMode>({
      query: (mode) => ({ url: `/purchase-contracts/preferences/columns/${encodeURIComponent(mode)}` }),
      providesTags: (_r, _e, mode) => [{ type: 'PurchaseContractPrefs', id: `columns:${mode}` }],
    }),
    putPurchaseContractColumnsPreference: builder.mutation<
      { message: string },
      { mode: PurchaseContractColumnsMode; columns: string[]; defaultColumns?: string[] }
    >({
      query: ({ mode, columns, defaultColumns }) => ({
        url: `/purchase-contracts/preferences/columns/${encodeURIComponent(mode)}`,
        method: 'PUT',
        body: { columns, ...(defaultColumns ? { defaultColumns } : {}) },
      }),
      invalidatesTags: (_r, _e, { mode }) => [{ type: 'PurchaseContractPrefs', id: `columns:${mode}` }],
    }),
    getPurchaseRepInfo: builder.query<PurchaseRepInfoResponse, void>({
      query: () => ({ url: '/purchase-contracts/rep-info' }),
      providesTags: ['PurchaseContractPrefs'],
    }),
    patchPurchaseContractListFilters: builder.mutation<{ message: string }, PurchaseContractListFiltersPatch>({
      query: (body) => ({
        url: '/purchase-contracts/list-filters',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['PurchaseContractPrefs'],
    }),
    setPurchaseRepInfo: builder.mutation<{ message: string }, number>({
      query: (signId) => ({
        url: '/purchase-contracts/rep-info',
        method: 'POST',
        body: { signId },
      }),
      invalidatesTags: ['PurchaseContractPrefs'],
    }),
  }),
})

export const {
  useGetPurchaseContractsQuery,
  useCreatePurchaseContractMutation,
  useUpdatePurchaseContractMutation,
  useCancelPurchaseContractMutation,
  useGetPurchaseReferenceRowsQuery,
  useGetPurchaseContractPagePreferencesQuery,
  useGetPurchaseContractColumnsPreferenceQuery,
  usePutPurchaseContractColumnsPreferenceMutation,
  useGetPurchaseRepInfoQuery,
  usePatchPurchaseContractListFiltersMutation,
  useSetPurchaseRepInfoMutation,
} = purchaseContractApi

// ===================== purchaseAnnexesApi =====================
/**
 * annexesApi — RTK Query slice cho /purchase/annexes/*.
 * Tag enhanced: 'Annex'. Đồng thời invalidate 'LocalP' / 'ContractAll' nếu cần.
 *
 * Endpoints (Sprint 5):
 *   GET    /purchase/annexes/list?contract_no=&annex_date_from=&annex_date_to=&page=&page_size=
 *   GET    /purchase/annexes/suggest-no?contract_no=
 *   POST   /purchase/annexes/create
 *   GET    /purchase/annexes/{id}
 *   PUT    /purchase/annexes/{id}
 *   DELETE /purchase/annexes/{id}
 *   GET    /purchase/annexes/{id}/print[?cancel=1]
 */

const annexEnhanced = baseApi.enhanceEndpoints({ addTagTypes: ['Annex'] })

export const annexesApi = annexEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listAnnexes: b.query<PageResponse<AnnexOut>, AnnexListQuery | void>({
      query: (params) => ({ url: '/purchase/annexes/list', params: params ?? {} }),
      providesTags: (res, _e, arg) => {
        const contractNo = (arg && (arg as AnnexListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((it) => ({ type: 'Annex' as const, id: it.id })),
              { type: 'Annex' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'Annex' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'Annex' as const, id: 'LIST' }]
      },
    }),
    getAnnex: b.query<DataResponse<AnnexOut>, number>({
      query: (id) => `/purchase/annexes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Annex', id }],
    }),
    suggestAnnexNo: b.query<{ suggestedNo: string }, { contract_no: string }>({
      query: ({ contract_no }) => ({
        url: '/purchase/annexes/suggest-no',
        params: { contract_no },
      }),
      transformResponse: (r: { suggestedNo?: string; suggested_no?: string }) => ({
        suggestedNo: r.suggestedNo ?? r.suggested_no ?? '',
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'Annex', id: `SUGGEST-${arg.contract_no}` },
      ],
    }),
    /** Pre-save check (skill access-suggest) — composite (contract_no, annex_no). */
    checkAnnexNoExists: b.query<
      { exists: boolean },
      { contractNo: string; annexNo: string }
    >({
      query: ({ contractNo, annexNo }) => ({
        url: '/purchase/annexes/check-no',
        params: { contractNo, annexNo },
      }),
      keepUnusedDataFor: 0,
    }),
    createAnnex: b.mutation<DataResponse<AnnexOut>, AnnexCreateIn>({
      query: (body) => ({
        url: '/purchase/annexes/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Annex', id: 'LIST' }],
    }),
    updateAnnex: b.mutation<
      DataResponse<AnnexOut>,
      { id: number; body: AnnexUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/annexes/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Annex', id: arg.id },
        { type: 'Annex', id: 'LIST' },
      ],
    }),
    deleteAnnex: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/annexes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Annex', id },
        { type: 'Annex', id: 'LIST' },
      ],
    }),
    /** Print Amendment (HTML preview flow — Access btnPrint_Click →
     *  `DoCmd.OpenReport "rpPurchaseAmendment"`). Response shape:
     *  `{ data: { pdf_base64: '', filename: 'AX-…-amendment.pdf', html: '…' }, message }`.
     *
     *  Chấp nhận cả `number` (signature mới) và `{ id, cancel? }` (legacy) để
     *  chịu được callers cũ / stale bundle. URL bỏ trailing `?` khi không có
     *  cancel param. Defensive: throw nếu id không phải int dương để tránh
     *  URL `/purchase/annexes/undefined/print`. */
    printAnnex: b.query<
      DataResponse<LocalPPrintResult>,
      number | { id: number; cancel?: boolean }
    >({
      query: (arg) => {
        const id = typeof arg === 'number' ? arg : arg?.id
        if (!Number.isInteger(id) || (id as number) <= 0) {
          throw new Error(`Invalid annex id: ${String(id)}`)
        }
        const cancel = typeof arg === 'object' && arg ? arg.cancel : undefined
        return {
          url: `/purchase/annexes/${id}/print`,
          ...(cancel ? { params: { cancel: 1 } } : {}),
        }
      },
    }),
  }),
})

export const {
  useListAnnexesQuery,
  useGetAnnexQuery,
  useSuggestAnnexNoQuery,
  useLazySuggestAnnexNoQuery,
  useLazyCheckAnnexNoExistsQuery,
  useCreateAnnexMutation,
  useUpdateAnnexMutation,
  useDeleteAnnexMutation,
  useLazyPrintAnnexQuery,
} = annexesApi

// ===================== purchaseBanksApi =====================
export const banksApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listBanks: b.query<PageResponse<Bank>, BankListQuery | void>({
      query: (params) => ({ url: '/purchase/refs/banks/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((bk) => ({ type: 'Bank' as const, id: bk.bank_code })),
              { type: 'Bank' as const, id: 'LIST' },
            ]
          : [{ type: 'Bank' as const, id: 'LIST' }],
    }),
    createBank: b.mutation<DataResponse<Bank>, Bank>({
      query: (body) => ({ url: '/purchase/refs/banks/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Bank', id: 'LIST' }],
    }),
    updateBank: b.mutation<DataResponse<Bank>, { bank_code: string; body: Partial<Bank> }>({
      query: ({ bank_code, body }) => ({
        url: `/purchase/refs/banks/${encodeURIComponent(bank_code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Bank', id: arg.bank_code },
        { type: 'Bank', id: 'LIST' },
      ],
    }),
    deleteBank: b.mutation<MessageResponse, string>({
      query: (bank_code) => ({
        url: `/purchase/refs/banks/${encodeURIComponent(bank_code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Bank', id: 'LIST' }],
    }),
  }),
})

export const {
  useListBanksQuery,
  useCreateBankMutation,
  useUpdateBankMutation,
  useDeleteBankMutation,
} = banksApi

// ===================== purchaseContractsAllApi =====================
/**
 * contractsAllApi — UNION list local_p + consignment_p (NPE).
 * Sprint 1: chỉ list. Filter q + paging.
 */

export const contractsAllApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listAllContracts: b.query<PageResponse<ContractAllItem>, ContractAllListQuery | void>({
      query: (params) => ({ url: '/purchase/contracts/all/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'ContractAll' as const, id: it.contract_no })),
              { type: 'ContractAll' as const, id: 'LIST' },
            ]
          : [{ type: 'ContractAll' as const, id: 'LIST' }],
    }),
  }),
})

export const { useListAllContractsQuery } = contractsAllApi

// ===================== purchaseLocalPApi =====================
/**
 * localPApi — RTK Query slice cho /purchase/local-p/*.
 * Tag types: 'LocalP' (list) + 'LocalPDetail' (single).
 */

export const localPApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listLocalP: b.query<PageResponse<LocalPListItem>, LocalPListQuery | void>({
      query: (params) => ({ url: '/purchase/local-p/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'LocalP' as const, id: it.contract_no })),
              { type: 'LocalP' as const, id: 'LIST' },
            ]
          : [{ type: 'LocalP' as const, id: 'LIST' }],
    }),
    suggestLocalPContractNo: b.query<{ contractNo: string }, void>({
      query: () => '/purchase/local-p/suggest-contract-no',
      transformResponse: (r: { suggestedNo?: string; suggested_no?: string }) => ({
        contractNo: r.suggestedNo ?? r.suggested_no ?? '',
      }),
      providesTags: [{ type: 'LocalP' as const, id: 'SUGGEST' }],
    }),
    /** Pre-save check (skill access-suggest): contract_no đã có active row chưa? */
    checkLocalPContractNoExists: b.query<{ exists: boolean }, { contractNo: string }>({
      query: ({ contractNo }) =>
        `/purchase/local-p/check-contract-no?contractNo=${encodeURIComponent(contractNo)}`,
      keepUnusedDataFor: 0,
    }),
    getLocalP: b.query<DataResponse<LocalPDetail>, string>({
      query: (contractNo) => `/purchase/local-p/${safePathEncode(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [
        { type: 'LocalPDetail', id: contractNo },
      ],
    }),
    createLocalP: b.mutation<DataResponse<LocalPDetail>, LocalPCreateIn>({
      query: (body) => ({
        url: '/purchase/local-p/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LocalP', id: 'LIST' }, { type: 'ContractAll', id: 'LIST' }],
    }),
    updateLocalP: b.mutation<
      DataResponse<LocalPDetail>,
      { contractNo: string; body: LocalPUpdateIn }
    >({
      query: ({ contractNo, body }) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalP', id: arg.contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: arg.contractNo },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    deleteLocalP: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'LocalP', id: contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: contractNo },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    copyLocalP: b.mutation<DataResponse<LocalPDetail>, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}/copy`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'LocalP', id: 'LIST' }, { type: 'ContractAll', id: 'LIST' }],
    }),
    forceCloseLocalP: b.mutation<
      DataResponse<LocalPDetail>,
      { contractNo: string; forcedCloseDate: string; note?: string }
    >({
      query: ({ contractNo, forcedCloseDate, note }) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}/force-close`,
        method: 'POST',
        // BE ForceCloseIn: forcedCloseDate (camel alias) + optional note.
        body: { forcedCloseDate, note: note ?? '' },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalP', id: arg.contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: arg.contractNo },
      ],
    }),
    /** Access `btnUnforced_Click` — admin only, unforce-close. */
    reopenLocalP: b.mutation<DataResponse<LocalPDetail>, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'LocalP', id: contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: contractNo },
      ],
    }),
    /** Access `btnFillingDocs_Click` — admin/allowlist, toggle is_ctrl_filldoc. */
    toggleFillingDocsLocalP: b.mutation<DataResponse<LocalPDetail>, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}/filling-docs`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'LocalP', id: contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: contractNo },
      ],
    }),
    /** Cancel contract — sets cancel_adjust = 0 (NOT a hard delete). */
    cancelLocalP: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'LocalP', id: contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: contractNo },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    /** Access evidence: form_frmPurchaseQryLocalPPr_Choose.txt. */
    getLocalPPrintChoose: b.query<DataResponse<LocalPPrintChooseData>, string>({
      query: (contractNo) => `/purchase/local-p/${safePathEncode(contractNo)}/print/contract/choose`,
    }),
    /** Sprint 6 sẽ render thật; Sprint 1 placeholder. */
    printLocalP: b.query<
      DataResponse<LocalPPrintResult>,
      { contractNo: string; kind: LocalPPrintKind; choose?: LocalPPrintChooseIn }
    >({
      query: ({ contractNo, kind, choose }) =>
        choose && kind === 'contract'
          ? {
              url: `/purchase/local-p/${safePathEncode(contractNo)}/print/contract/preview`,
              method: 'POST',
              body: choose,
            }
          : `/purchase/local-p/${safePathEncode(contractNo)}/print/${kind}`,
    }),
    /** Email-permissioned hard-delete access (legacy: btnDelete2.Visible rule). */
    getLocalPCanHardDelete: b.query<DataResponse<{ can_delete: boolean }>, void>({
      query: () => '/purchase/local-p/access/can-delete',
    }),
  }),
})

export const {
  useListLocalPQuery,
  useSuggestLocalPContractNoQuery,
  useLazyCheckLocalPContractNoExistsQuery,
  useLazySuggestLocalPContractNoQuery,
  useGetLocalPQuery,
  useLazyGetLocalPQuery,
  useCreateLocalPMutation,
  useUpdateLocalPMutation,
  useDeleteLocalPMutation,
  useCancelLocalPMutation,
  useCopyLocalPMutation,
  useForceCloseLocalPMutation,
  useReopenLocalPMutation,
  useToggleFillingDocsLocalPMutation,
  useLazyGetLocalPPrintChooseQuery,
  useLazyPrintLocalPQuery,
  useGetLocalPCanHardDeleteQuery,
} = localPApi

// ===================== purchaseLocalPForwardsApi =====================
/**
 * localPForwardsApi — RTK Query slice cho /purchase/local-p-forwards/*.
 * Tag type: 'LocalPForward'. Đồng thời invalidate 'LocalPVer2' (tổng hợp top-up alert).
 */

export const localPForwardsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listForwards: b.query<PageResponse<LocalPForwardOut>, LocalPForwardListQuery | void>({
      query: (params) => ({
        url: '/purchase/local-p-forwards/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo = (arg && (arg as LocalPForwardListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((f) => ({ type: 'LocalPForward' as const, id: f.id })),
              { type: 'LocalPForward' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'LocalPForward' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'LocalPForward' as const, id: 'LIST' }]
      },
    }),
    createForward: b.mutation<DataResponse<LocalPForwardOut>, LocalPForwardCreateIn>({
      query: (body) => ({
        url: '/purchase/local-p-forwards/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'LocalPForward', id: 'LIST' },
        { type: 'LocalPVer2', id: 'LIST' },
      ],
    }),
    updateForward: b.mutation<
      DataResponse<LocalPForwardOut>,
      { forwardId: number; body: LocalPForwardUpdateIn }
    >({
      query: ({ forwardId, body }) => ({
        url: `/purchase/local-p-forwards/${forwardId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalPForward', id: arg.forwardId },
        { type: 'LocalPForward', id: 'LIST' },
        { type: 'LocalPVer2', id: 'LIST' },
      ],
    }),
    deleteForward: b.mutation<MessageResponse, number>({
      query: (forwardId) => ({
        url: `/purchase/local-p-forwards/${forwardId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, forwardId) => [
        { type: 'LocalPForward', id: forwardId },
        { type: 'LocalPForward', id: 'LIST' },
        { type: 'LocalPVer2', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListForwardsQuery,
  useCreateForwardMutation,
  useUpdateForwardMutation,
  useDeleteForwardMutation,
} = localPForwardsApi

// ===================== purchaseLocalPPlansApi =====================
/**
 * localPPlansApi — RTK Query slice cho /purchase/local-p-plans/*.
 * Tag type: 'LocalPPlan'.
 */

export const localPPlansApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPlans: b.query<PageResponse<LocalPPlanListItem>, LocalPPlanListQuery | void>({
      query: (params) => ({
        url: '/purchase/local-p-plans/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo = (arg && (arg as LocalPPlanListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items
                .filter((p): p is LocalPPlanListItem & { id: number } => p.id != null)
                .map((p) => ({ type: 'LocalPPlan' as const, id: p.id })),
              { type: 'LocalPPlan' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'LocalPPlan' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'LocalPPlan' as const, id: 'LIST' }]
      },
    }),
    createPlan: b.mutation<DataResponse<LocalPPlanOut>, LocalPPlanCreateIn>({
      query: (body) => ({
        url: '/purchase/local-p-plans/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LocalPPlan', id: 'LIST' }],
    }),
    updatePlan: b.mutation<
      DataResponse<LocalPPlanOut>,
      { planId: number; body: LocalPPlanUpdateIn }
    >({
      query: ({ planId, body }) => ({
        url: `/purchase/local-p-plans/${planId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalPPlan', id: arg.planId },
        { type: 'LocalPPlan', id: 'LIST' },
      ],
    }),
    deletePlan: b.mutation<MessageResponse, number>({
      query: (planId) => ({
        url: `/purchase/local-p-plans/${planId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, planId) => [
        { type: 'LocalPPlan', id: planId },
        { type: 'LocalPPlan', id: 'LIST' },
      ],
    }),
    deleteAllPlans: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p-plans/by-contract/${encodeURIComponent(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'LocalPPlan', id: 'LIST' }],
    }),
  }),
})

export const {
  useListPlansQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
  useDeleteAllPlansMutation,
} = localPPlansApi

// ===================== purchaseLocalPVer2Api =====================
/**
 * localPVer2Api — RTK Query slice cho /purchase/local-p-ver2/list.
 * View tổng hợp Forward P + Top-up Alert.
 * Tag type: 'LocalPVer2'.
 */

export const localPVer2Api = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listLocalPVer2: b.query<PageResponse<LocalPVer2Item>, LocalPVer2ListQuery | void>({
      query: (params) => ({
        url: '/purchase/local-p-ver2/list',
        params: params ?? {},
      }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({
                type: 'LocalPVer2' as const,
                id: it.contract_no,
              })),
              { type: 'LocalPVer2' as const, id: 'LIST' },
            ]
          : [{ type: 'LocalPVer2' as const, id: 'LIST' }],
    }),
  }),
})

export const { useListLocalPVer2Query } = localPVer2Api

// ===================== purchaseNpeAllocationsApi =====================
/**
 * npeAllocationsApi — RTK Query slice cho /purchase/npe-allocations/*.
 * Tag types: 'NPEAlloc'. Cũng invalidate 'NPEFixation' (count) và 'LocalP' khi commit.
 *
 * Wizard flow (SOP §7.8):
 *  1. POST /sessions/create { fixation_id } -> nhận session_uid + lines snapshot
 *  2. PUT  /sessions/{uid}/lines { lines: [...] } (debounced 500ms khi user gõ)
 *  3. POST /sessions/{uid}/commit -> tạo committed allocations + invalidate
 *  4. DELETE /sessions/{uid} (cancel)
 *  5. DELETE /{allocation_id} — soft-delete 1 committed allocation
 */

export const npeAllocationsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listNPEAllocations: b.query<
      PageResponse<NPEAllocationOut>,
      NPEAllocationListQuery | void
    >({
      query: (params) => ({
        url: '/purchase/npe-allocations/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const fix = (arg && (arg as NPEAllocationListQuery).fixation_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((a) => ({
                type: 'NPEAlloc' as const,
                id: a.allocation_id,
              })),
              { type: 'NPEAlloc' as const, id: `LIST-${fix}` },
            ]
          : [{ type: 'NPEAlloc' as const, id: `LIST-${fix}` }]
        return [...list, { type: 'NPEAlloc' as const, id: 'LIST' }]
      },
    }),
    /** Đọc nhanh allocations theo fixation_no (dùng cho NPEFixationDetail / wizard step 2). */
    getNPEAllocationsByFixation: b.query<DataResponse<NPEAllocationOut[]>, string>({
      query: (fixationNo) =>
        `/purchase/npe-allocations/by-fixation/${encodeURIComponent(fixationNo)}`,
      providesTags: (_r, _e, fixationNo) => [
        { type: 'NPEAlloc', id: `by-fix-${fixationNo}` },
      ],
    }),

    /** ---- Session APIs ---- */
    createAllocationSession: b.mutation<
      DataResponse<NPEAllocationSessionState>,
      { fixation_id: number }
    >({
      query: (body) => ({
        url: '/purchase/npe-allocations/sessions/create',
        method: 'POST',
        body,
      }),
      // Không invalidate gì (session chưa commit, không thay đổi list)
    }),
    getAllocationSession: b.query<DataResponse<NPEAllocationSessionState>, string>({
      query: (sessionUid) => `/purchase/npe-allocations/sessions/${sessionUid}`,
      providesTags: (_r, _e, uid) => [{ type: 'NPEAlloc', id: `session-${uid}` }],
    }),
    updateSessionLines: b.mutation<
      DataResponse<NPEAllocationSessionState>,
      { sessionUid: string; lines: NPEAllocationLineIn[]; note?: string | null; advancedDate?: string | null }
    >({
      query: ({ sessionUid, lines, note, advancedDate }) => ({
        url: `/purchase/npe-allocations/sessions/${sessionUid}/lines`,
        method: 'PUT',
        body: { lines, note: note ?? undefined, advanced_date: advancedDate ?? undefined },
      }),
      // Cập nhật cache session theo response
      async onQueryStarted({ sessionUid }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            npeAllocationsApi.util.updateQueryData(
              'getAllocationSession',
              sessionUid,
              (draft) => {
                if (data?.data) draft.data = data.data
              },
            ),
          )
        } catch {
          // ignore
        }
      },
    }),
    commitAllocationSession: b.mutation<
      DataResponse<NPEAllocationOut[]>,
      { sessionUid: string; body: NPEAllocationCommitIn }
    >({
      query: ({ sessionUid, body }) => ({
        url: `/purchase/npe-allocations/sessions/${sessionUid}/commit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NPEAlloc', id: 'LIST' },
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPForward', id: 'LIST' },
      ],
    }),
    cancelAllocationSession: b.mutation<MessageResponse, string>({
      query: (sessionUid) => ({
        url: `/purchase/npe-allocations/sessions/${sessionUid}`,
        method: 'DELETE',
      }),
    }),

    /** Soft-delete 1 committed allocation. */
    deleteNPEAllocation: b.mutation<MessageResponse, number>({
      query: (allocationId) => ({
        url: `/purchase/npe-allocations/${allocationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'NPEAlloc', id },
        { type: 'NPEAlloc', id: 'LIST' },
        { type: 'NPEFixation', id: 'LIST' },
      ],
    }),

    /** Bulk soft-delete all allocations cho fixation_no (Access btnDelete). */
    deleteNPEAllocationsByFixation: b.mutation<MessageResponse, string>({
      query: (fixationNo) => ({
        url: `/purchase/npe-allocations/by-fixation/${encodeURIComponent(fixationNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'NPEAlloc', id: 'LIST' },
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPForward', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListNPEAllocationsQuery,
  useGetNPEAllocationsByFixationQuery,
  useCreateAllocationSessionMutation,
  useGetAllocationSessionQuery,
  useUpdateSessionLinesMutation,
  useCommitAllocationSessionMutation,
  useCancelAllocationSessionMutation,
  useDeleteNPEAllocationMutation,
  useDeleteNPEAllocationsByFixationMutation,
} = npeAllocationsApi

// ===================== purchaseNpeContractsApi =====================
/**
 * npeContractsApi — RTK Query slice cho /purchase/npe-contracts/*.
 * Tag types: 'NPEContract' (list + detail).
 *
 * **Backend dùng `contract_no` (NPE-XXXXX) làm path key, không phải numeric id.**
 *   GET    /purchase/npe-contracts/list?...
 *   GET    /purchase/npe-contracts/suggest-contract-no
 *   POST   /purchase/npe-contracts/create
 *   GET    /purchase/npe-contracts/{contract_no}
 *   PUT    /purchase/npe-contracts/{contract_no}
 *   DELETE /purchase/npe-contracts/{contract_no}
 *   POST   /purchase/npe-contracts/{contract_no}/copy
 *   GET    /purchase/npe-contracts/{contract_no}/print
 */

const enc = safePathEncode

export const npeContractsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listNPEContracts: b.query<PageResponse<NPEContractOut>, NPEContractListQuery | void>({
      query: (params) => ({
        url: '/purchase/npe-contracts/list',
        params: params ?? {},
      }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({
                type: 'NPEContract' as const,
                id: it.contract_no,
              })),
              { type: 'NPEContract' as const, id: 'LIST' },
            ]
          : [{ type: 'NPEContract' as const, id: 'LIST' }],
    }),
    /** Defaults cho form New: contract_no (`NPE-XXXXX`) + interest_rate_note
     *  template. Backend mirror Access btnNew_Click trong
     *  `frmPurchaseMainSubFormNPE`: `Suggest_IndexName("consignment_p")` +
     *  `DLookup("value", "public_tmp_add_information", "type='procurement_npe_pr1'")`.
     *
     *  Tag `SUGGEST` được invalidate khi create thành công → Copy/New lần sau
     *  sẽ refetch contract_no mới (parity Access: `Suggest_IndexName` gọi mỗi
     *  lần btnNew/btnCopy_Click). */
    suggestNPEContractNo: b.query<{ contractNo: string; interestRateNote: string }, void>({
      query: () => '/purchase/npe-contracts/suggest-contract-no',
      transformResponse: (r: {
        suggestedNo?: string
        suggested_no?: string
        interestRateNote?: string
        interest_rate_note?: string
      }) => ({
        contractNo: r.suggestedNo ?? r.suggested_no ?? '',
        interestRateNote: r.interestRateNote ?? r.interest_rate_note ?? '',
      }),
      providesTags: [{ type: 'NPEContract' as const, id: 'SUGGEST' }],
    }),
    /** Pre-save check (skill access-suggest): contract_no đã có active row chưa? */
    checkNPEContractNoExists: b.query<{ exists: boolean }, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/npe-contracts/check-contract-no',
        params: { contractNo },
      }),
      keepUnusedDataFor: 0,
    }),
    getNPEContract: b.query<DataResponse<NPEContractOut>, string>({
      query: (contractNo) => `/purchase/npe-contracts/${enc(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [{ type: 'NPEContract', id: contractNo }],
    }),
    /**
     * Single-row lookup từ v_consignment_p, BYPASS page-level date filter ở
     * `/list` (delivery_time prefs). Dùng autofill dialog NPE Fixation
     * (`supplier_name` + `to_be_fix`): nếu user pick NPE ngoài date range của
     * page, `.find()` trên `useListNPEContractsQuery` trả null → autofill rỗng.
     * `keepUnusedDataFor: 0` + `refetchOnMountOrArgChange: true` ⇒ fresh mỗi
     * lần mở dialog hoặc đổi npe_no. Cùng pattern Futures Allocation detail.
     */
    getNPEContractDetails: b.query<DataResponse<NPEContractOut>, string>({
      query: (contractNo) => `/purchase/npe-contracts/details/${enc(contractNo)}`,
      keepUnusedDataFor: 0,
      providesTags: (_r, _e, contractNo) => [{ type: 'NPEContract', id: contractNo }],
    }),
    createNPEContract: b.mutation<DataResponse<NPEContractOut>, NPEContractCreateIn>({
      query: (body) => ({
        url: '/purchase/npe-contracts/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NPEContract', id: 'LIST' },
        { type: 'NPEContract', id: 'SUGGEST' },
      ],
    }),
    updateNPEContract: b.mutation<
      DataResponse<NPEContractOut>,
      { contract_no: string; body: NPEContractUpdateIn }
    >({
      query: ({ contract_no, body }) => ({
        url: `/purchase/npe-contracts/${enc(contract_no)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'NPEContract', id: arg.contract_no },
        { type: 'NPEContract', id: 'LIST' },
      ],
    }),
    deleteNPEContract: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/npe-contracts/${enc(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'NPEContract', id: contractNo },
        { type: 'NPEContract', id: 'LIST' },
      ],
    }),
    copyNPEContract: b.mutation<DataResponse<NPEContractOut>, string>({
      query: (contractNo) => ({
        url: `/purchase/npe-contracts/${enc(contractNo)}/copy`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'NPEContract', id: 'LIST' }],
    }),
    /** Print PDF — backend trả về base64. */
    printNPEContract: b.query<DataResponse<LocalPPrintResult>, string>({
      query: (contractNo) => `/purchase/npe-contracts/${enc(contractNo)}/print`,
    }),
  }),
})

export const {
  useListNPEContractsQuery,
  useSuggestNPEContractNoQuery,
  useLazySuggestNPEContractNoQuery,
  useLazyCheckNPEContractNoExistsQuery,
  useGetNPEContractQuery,
  useGetNPEContractDetailsQuery,
  useCreateNPEContractMutation,
  useUpdateNPEContractMutation,
  useDeleteNPEContractMutation,
  useCopyNPEContractMutation,
  useLazyPrintNPEContractQuery,
} = npeContractsApi

// ===================== purchaseNpeFixationsApi =====================
/**
 * npeFixationsApi — RTK Query slice cho /purchase/npe-fixations/*.
 * Tag types: 'NPEFixation'. Cũng invalidate 'NPEContract', 'LocalP' vì
 * fixation thay đổi tracking (received/fixed/to_be_fixed) của NPE và sinh LocalP mới.
 *
 * Sprint 4 (SOP §7.7, Table 58) — endpoints:
 *   GET    /purchase/npe-fixations/list?contract_no=&page=&page_size=
 *   POST   /purchase/npe-fixations/create
 *   POST   /purchase/npe-fixations/create-and-allocate
 *   GET    /purchase/npe-fixations/{id}
 *   PUT    /purchase/npe-fixations/{id}
 *   DELETE /purchase/npe-fixations/{id}
 *   GET    /purchase/npe-fixations/{id}/allocations-count
 *   GET    /purchase/npe-fixations/{id}/print
 */

export const npeFixationsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listNPEFixations: b.query<PageResponse<NPEFixationOut>, NPEFixationListQuery | void>({
      query: (params) => ({
        url: '/purchase/npe-fixations/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo = (arg && (arg as NPEFixationListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((f) => ({
                type: 'NPEFixation' as const,
                id: f.cs_fixation_id,
              })),
              { type: 'NPEFixation' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'NPEFixation' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'NPEFixation' as const, id: 'LIST' }]
      },
    }),
    getNPEFixation: b.query<DataResponse<NPEFixationOut>, number>({
      query: (id) => `/purchase/npe-fixations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'NPEFixation', id }],
    }),
    createNPEFixation: b.mutation<DataResponse<NPEFixationOut>, NPEFixationCreateIn>({
      query: (body) => ({
        url: '/purchase/npe-fixations/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'NPEContract', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    /**
     * Tạo fixation + LocalP + open allocation session (atomic).
     * Trả về session_uid để FE navigate sang Allocation Wizard.
     */
    createAndAllocateNPEFixation: b.mutation<
      DataResponse<NPEFixationCreateAndAllocateOut>,
      NPEFixationCreateIn
    >({
      query: (body) => ({
        url: '/purchase/npe-fixations/create-and-allocate',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'NPEContract', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    updateNPEFixation: b.mutation<
      DataResponse<NPEFixationOut>,
      { id: number; body: NPEFixationUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/npe-fixations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'NPEFixation', id: arg.id },
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'NPEContract', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
      ],
    }),
    deleteNPEFixation: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/npe-fixations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'NPEFixation', id },
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'NPEContract', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
      ],
    }),
    /** Số allocations đã commit của 1 fixation. */
    getNPEFixationAllocationsCount: b.query<
      DataResponse<NPEFixationAllocationsCountOut>,
      number
    >({
      query: (id) => `/purchase/npe-fixations/${id}/allocations-count`,
      providesTags: (_r, _e, id) => [
        { type: 'NPEFixation', id: `count-${id}` },
        { type: 'NPEAlloc', id: `fixation-${id}` },
      ],
    }),
    /** Print "PHỤ LỤC HỢP ĐỒNG" — rpPurchaseQryNPEFixation. BE trả HTML qua
     *  PrintResult.html, FE hiển thị qua iframe srcDoc. */
    printNPEFixation: b.query<DataResponse<LocalPPrintResult>, number>({
      query: (id) => `/purchase/npe-fixations/${id}/print`,
    }),
    /** Gợi ý (annex_index, fixation_no) cho NPE Fixation kế tiếp của 1 NPE no.
     *  Access parity với `Suggest_IndexName("phulucindex"|"phuluc" & npe_no)`. */
    suggestNPEFixationNo: b.query<DataResponse<NPEFixationSuggestOut>, string>({
      query: (npe_no) => ({
        url: '/purchase/npe-fixations/suggest',
        params: { npe_no },
      }),
      // Phụ thuộc danh sách fixation hiện có; khi tạo/xóa fixation phải refetch.
      providesTags: (_r, _e, npe_no) => [
        { type: 'NPEFixation', id: `SUGGEST-${npe_no}` },
        { type: 'NPEFixation', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListNPEFixationsQuery,
  useGetNPEFixationQuery,
  useLazyGetNPEFixationQuery,
  useCreateNPEFixationMutation,
  useCreateAndAllocateNPEFixationMutation,
  useUpdateNPEFixationMutation,
  useDeleteNPEFixationMutation,
  useGetNPEFixationAllocationsCountQuery,
  useLazyPrintNPEFixationQuery,
  useSuggestNPEFixationNoQuery,
} = npeFixationsApi

// ===================== purchasePtbfFixationsApi =====================
/**
 * ptbfFixationsApi — RTK Query slice cho /purchase/ptbf-fixations/*.
 * Tag type: 'PTBFFixation'.
 *
 * Sprint 3 — endpoints:
 *   GET    /purchase/ptbf-fixations/list?contract_no=&futures_month=&has_uploaded=&page=&page_size=
 *   POST   /purchase/ptbf-fixations/create
 *   GET    /purchase/ptbf-fixations/{ptbf_p_id}
 *   PUT    /purchase/ptbf-fixations/{ptbf_p_id}
 *   DELETE /purchase/ptbf-fixations/{ptbf_p_id}
 *   GET    /purchase/ptbf-fixations/{ptbf_p_id}/print           (placeholder)
 *   GET    /purchase/ptbf-fixations/{ptbf_p_id}/print/cancel
 *   GET    /purchase/ptbf-fixations/{ptbf_p_id}/print/sub
 */

export type PTBFFixationPrintKind = 'main' | 'cancel' | 'sub'

export interface PTBFLotTobeFixedOut {
  contract_no: string
  total_lot_tobe_fixed: number | null
  total_lot: number | null
  // is_upload / issued_date áp lên TOÀN contract_no — trả về từ lần fixation
  // gần nhất để pre-fill khi New chọn contract đã upload/issued trước đó.
  is_upload: number | null
  issued_date: string | null
}

export const ptbfFixationsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPTBFFixations: b.query<
      PageResponse<PTBFFixationOut>,
      PTBFFixationListQuery | void
    >({
      query: (params) => ({
        url: '/purchase/ptbf-fixations/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo =
          (arg && (arg as PTBFFixationListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((it) => ({
                type: 'PTBFFixation' as const,
                id: it.ptbf_p_id,
              })),
              { type: 'PTBFFixation' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'PTBFFixation' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'PTBFFixation' as const, id: 'LIST' }]
      },
    }),
    getPTBFFixation: b.query<DataResponse<PTBFFixationOut>, number>({
      query: (id) => `/purchase/ptbf-fixations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PTBFFixation', id }],
    }),
    createPTBFFixation: b.mutation<
      DataResponse<PTBFFixationOut>,
      PTBFFixationCreateIn
    >({
      query: (body) => ({
        url: '/purchase/ptbf-fixations/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'PTBFFixation', id: 'LIST' }],
    }),
    updatePTBFFixation: b.mutation<
      DataResponse<PTBFFixationOut>,
      { id: number; body: PTBFFixationUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/ptbf-fixations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'PTBFFixation', id: arg.id },
        { type: 'PTBFFixation', id: 'LIST' },
      ],
    }),
    deletePTBFFixation: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/ptbf-fixations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'PTBFFixation', id },
        { type: 'PTBFFixation', id: 'LIST' },
      ],
    }),
    /** Lookup remaining lot_tobe_fixed (+ total_lot) per contract — match Access
     *  contract_no_AfterUpdate / btnEdit_Click. */
    getPTBFLotTobeFixed: b.query<
      DataResponse<PTBFLotTobeFixedOut>,
      { contract_no: string }
    >({
      query: ({ contract_no }) => ({
        url: '/purchase/ptbf-fixations/lot-tobe-fixed',
        params: { contract_no },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'PTBFFixation' as const, id: `TBF-${arg.contract_no}` },
      ],
    }),
    /** Print PDF (placeholder Sprint 6). `kind` = 'main' | 'cancel' | 'sub'. */
    printPTBFFixation: b.query<
      DataResponse<LocalPPrintResult>,
      { id: number; kind: PTBFFixationPrintKind }
    >({
      query: ({ id, kind }) => {
        if (kind === 'main') return `/purchase/ptbf-fixations/${id}/print`
        return `/purchase/ptbf-fixations/${id}/print/${kind}`
      },
    }),
  }),
})

export const {
  useListPTBFFixationsQuery,
  useGetPTBFFixationQuery,
  useCreatePTBFFixationMutation,
  useUpdatePTBFFixationMutation,
  useDeletePTBFFixationMutation,
  useLazyPrintPTBFFixationQuery,
  useLazyGetPTBFLotTobeFixedQuery,
} = ptbfFixationsApi

// ===================== purchasePtbfRollingsApi =====================
/**
 * ptbfRollingsApi — RTK Query slice cho /purchase/ptbf-rollings/*.
 * Tag type: 'PTBFRolling'.
 *
 * Sprint 3 — endpoints:
 *   GET    /purchase/ptbf-rollings/list?contract_no=&page=&page_size=
 *   POST   /purchase/ptbf-rollings/create
 *   GET    /purchase/ptbf-rollings/{rolling_id}
 *   PUT    /purchase/ptbf-rollings/{rolling_id}
 *   DELETE /purchase/ptbf-rollings/{rolling_id}
 *   GET    /purchase/ptbf-rollings/{rolling_id}/print
 *   GET    /purchase/ptbf-rollings/{rolling_id}/print/cancel
 */

export type PTBFRollingPrintKind = 'main' | 'cancel'

export const ptbfRollingsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPTBFRollings: b.query<
      PageResponse<PTBFRollingOut>,
      PTBFRollingListQuery | void
    >({
      query: (params) => ({
        url: '/purchase/ptbf-rollings/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo =
          (arg && (arg as PTBFRollingListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((it) => ({
                type: 'PTBFRolling' as const,
                id: it.id,
              })),
              { type: 'PTBFRolling' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'PTBFRolling' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'PTBFRolling' as const, id: 'LIST' }]
      },
    }),
    getPTBFRolling: b.query<DataResponse<PTBFRollingOut>, number>({
      query: (id) => `/purchase/ptbf-rollings/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PTBFRolling', id }],
    }),
    createPTBFRolling: b.mutation<
      DataResponse<PTBFRollingOut>,
      PTBFRollingCreateIn
    >({
      query: (body) => ({
        url: '/purchase/ptbf-rollings/create',
        method: 'POST',
        body,
      }),
      // Save Roll cập nhật HĐ Purchase (local_p): futures_month + ptbf_diffs mới
      // + auto-tạo annex. Phải invalidate list HĐ (LocalP + ContractAll) VÀ
      // detail (LocalPDetail) để UI không hiện Futures Month/Diffs cũ.
      invalidatesTags: (res, _e, arg) => {
        const cn = res?.data?.contract_no ?? arg.contract_no
        return [
          { type: 'PTBFRolling', id: 'LIST' },
          { type: 'LocalP', id: 'LIST' },
          { type: 'LocalP', id: cn },
          { type: 'LocalPDetail', id: cn },
          { type: 'ContractAll', id: 'LIST' },
        ]
      },
    }),
    updatePTBFRolling: b.mutation<
      DataResponse<PTBFRollingOut>,
      { id: number; body: PTBFRollingUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/ptbf-rollings/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (res, _e, arg) => {
        const cn = res?.data?.contract_no
        return [
          { type: 'PTBFRolling', id: arg.id },
          { type: 'PTBFRolling', id: 'LIST' },
          { type: 'LocalP', id: 'LIST' },
          { type: 'ContractAll', id: 'LIST' },
          ...(cn
            ? [
                { type: 'LocalP' as const, id: cn },
                { type: 'LocalPDetail' as const, id: cn },
              ]
            : []),
        ]
      },
    }),
    deletePTBFRolling: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/ptbf-rollings/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'PTBFRolling', id },
        { type: 'PTBFRolling', id: 'LIST' },
      ],
    }),
    /** Print PDF (placeholder Sprint 6). `kind` = 'main' | 'cancel'. */
    printPTBFRolling: b.query<
      DataResponse<LocalPPrintResult>,
      { id: number; kind: PTBFRollingPrintKind }
    >({
      query: ({ id, kind }) => {
        if (kind === 'main') return `/purchase/ptbf-rollings/${id}/print`
        return `/purchase/ptbf-rollings/${id}/print/${kind}`
      },
    }),
  }),
})

export const {
  useListPTBFRollingsQuery,
  useGetPTBFRollingQuery,
  useCreatePTBFRollingMutation,
  useUpdatePTBFRollingMutation,
  useDeletePTBFRollingMutation,
  useLazyPrintPTBFRollingQuery,
} = ptbfRollingsApi
